import { IrdEntitySchema } from '../metadata/metadata';
import { IrdProperty, IrdPropertyType } from '../metadata/property';
import { PagedData } from '../paged-data';
import { genericMap, Omitter } from './generic-map';

export type DaoType = Record<string, any>;
export type DtoType = Record<string, any>;

export interface SyncDtoMapper<Dao extends DaoType, Dto extends DtoType> {
  fromPagedDao(pagedData: PagedData<Dao>, ...args: unknown[]): PagedData<Dto>;

  fromDao(dao: Dao, ...args: unknown[]): Dto;

  fromDao(dao: Dao | null, ...args: unknown[]): Dto | null;

  fromDaos(daos: Dao[]): Dto[];

  toDao(dto: Dto, ...args: unknown[]): Dao;

  toDao(dto: Dto | null, ...args: unknown[]): Dao | null;
}

export class DefaultSyncDtoMapper<Dao extends DaoType, Dto extends DtoType> implements SyncDtoMapper<Dao, Dto> {
  // Maps, not object literals: a field named `constructor` or `toString` would otherwise hit
  // Object.prototype instead of falling back to its own name.
  private readonly daoKeyByDtoKey = new Map<string, string>();
  private readonly dtoKeyByDaoKey = new Map<string, string>();

  /**
   * @param schema the DTO's metadata, read only for the backend field names it declares. Without
   *   it both sides are assumed to name every field alike, which is the behaviour of every mapper
   *   that passes no schema.
   */
  constructor(
    protected dtoSample: Dto,
    protected daoSample: Dao,
    protected schema?: IrdEntitySchema<Dto>,
  ) {
    for (const property of Object.values(schema?.properties ?? {}) as IrdProperty<Dto, IrdPropertyType>[]) {
      if (!property.daoName) {
        continue;
      }
      // A misspelt backend name would otherwise read as absent on every DAO and never be written
      // back: the value is lost in both directions without a trace.
      if (!(property.daoName in daoSample)) {
        throw new Error(`Property '${property.nameAsString()}' declares daoName '${property.daoName}', which the DAO sample does not have`);
      }
      this.daoKeyByDtoKey.set(property.name.toString(), property.daoName);
      this.dtoKeyByDaoKey.set(property.daoName, property.name.toString());
    }
  }

  /** The name the backend knows a DTO field by. */
  private daoKeyOf(dtoKey: string): string {
    return this.daoKeyByDtoKey.get(dtoKey) ?? dtoKey;
  }

  /** The name the DTO knows a backend field by. */
  private dtoKeyOf(daoKey: string): string {
    return this.dtoKeyByDaoKey.get(daoKey) ?? daoKey;
  }

  public fromDao(dao: Dao, ...args: unknown[]): Dto;
  public fromDao(dao: Dao | null, ...args: unknown[]): Dto | null;
  public fromDao(dao: Dao | null, ...args: unknown[]): Dto | null {
    if (dao) {
      return this.fromDaoUnsafe(dao, ...args);
    } else {
      return null;
    }
  }

  public fromDaos(daos: Dao[]): Dto[] {
    if (daos) {
      return daos.map((dao) => this.fromDao(dao));
    } else {
      return [];
    }
  }

  public toDao(dto: Dto, ...args: unknown[]): Dao;
  public toDao(dto: Dto | null, ...args: unknown[]): Dao | null;
  public toDao(dto: Dto | null, ...args: unknown[]): Dao | null {
    if (dto) {
      return this.toDaoUnsafe(dto, ...args);
    } else {
      return null;
    }
  }

  protected toDaoUnsafe(dto: Dto, ..._args: unknown[]): Dao {
    return genericMap<Dto, Dao>(dto, this.getToAddDao(dto), this.daoSample, (key) => this.dtoKeyOf(key));
  }

  public fromPagedDao(pagedData: PagedData<Dao>, ..._args: []): PagedData<Dto> {
    return {
      //in pagedData content no null values are allowed
      content: pagedData?.content?.map((dao) => this.fromDaoUnsafe(dao)),
      totalPages: pagedData?.totalPages,
      totalElements: pagedData?.totalElements,
      number: pagedData?.number,
      size: pagedData?.size,
      first: pagedData?.first,
      last: pagedData?.last,
    };
  }

  protected getToAddDto(dao: Dao): Omitter<Dao, Dto> {
    const toAdd: Partial<Dto> = {};

    for (const key in this.dtoSample) {
      // Presence is checked under the name the backend uses. Checking it under the DTO's own name
      // reports a renamed field as absent and overwrites the value that did arrive with the
      // sample's default.
      if (!(this.daoKeyOf(key) in dao)) {
        toAdd[key] = this.dtoSample[key];
      }
    }

    return toAdd as Omitter<Dao, Dto>;
  }

  protected getToAddDao(dto: Dto): Omitter<Dto, Dao> {
    const toAdd: Partial<Dao> = {};

    for (const key in this.daoSample) {
      if (!(this.dtoKeyOf(key) in dto)) {
        toAdd[key] = this.daoSample[key];
      }
    }

    return toAdd as Omitter<Dto, Dao>;
  }

  protected fromDaoUnsafe(dao: Dao, ..._args: unknown[]): Dto {
    return genericMap<Dao, Dto>(dao, this.getToAddDto(dao), this.dtoSample, (key) => this.daoKeyOf(key));
  }
}
